import React, { useState } from "react";
import { Pagination, EmptyTable, Loader } from "../../../components";
import { useGetModulesQuery } from "../../../store/auth/authApi";
import ModuleModal from "./ModuleModal";
import SubModuleModal from "./SubModuleModal";

export default function ModulesList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isSubModuleModalOpen, setIsSubModuleModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  const {
    data: modulesData,
    isLoading: modulesLoading,
    error: modulesError,
  } = useGetModulesQuery({ page: currentPage, all: 1, per_page: 200 });

  const filteredModules = Array.isArray(modulesData?.data)
    ? modulesData.data.filter((module) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          module.name?.toLowerCase().includes(query) ||
          module.url?.toLowerCase().includes(query)
        );
      })
    : [];

  const handleCreateModule = () => {
    setEditingModule(null);
    setIsModuleModalOpen(true);
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setIsModuleModalOpen(true);
  };

  const handleManageSubModules = (module) => {
    setSelectedModule(module);
    setIsSubModuleModalOpen(true);
  };

  const handleModuleSuccess = () => {
    setIsModuleModalOpen(false);
    setEditingModule(null);
  };

  const handleSubModuleSuccess = () => {
    // Keep modal open so staff can add more; list refreshes via cache tags
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Modules</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-3xl font-bold">
                    {modulesData?.total || filteredModules.length || 0}
                  </p>
                  <p className="text-gray-300">Total Modules</p>
                </div>
              </div>
              <button
                onClick={handleCreateModule}
                className="bg-[#8B313A] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Create new module
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Module catalog
          </h3>
          <p className="text-sm text-gray-600">
            Modules become available as soon as they exist in the catalog. There
            is no separate publish step. Assign them to plans on{" "}
            <span className="font-medium text-gray-700">Subscriptions</span>, or
            override per cooperative on the cooperative details page.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 sm:mb-0">
              Module List
            </h2>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search your query here"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#8B313A] focus:border-[#8B313A] sm:text-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {modulesLoading ? (
            <div className="py-8">
              <Loader size="md" color="primary" text="Loading modules..." />
            </div>
          ) : modulesError ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">
                Error loading modules:{" "}
                {modulesError?.data?.message ||
                  modulesError?.message ||
                  "Unknown error"}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-[#8B313A] text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : filteredModules.length === 0 ? (
            <EmptyTable
              message={
                searchQuery
                  ? `No modules found matching "${searchQuery}"`
                  : "No modules available"
              }
            />
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
              {filteredModules.map((module) => (
                <div
                  key={module.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-gray-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-800">
                        {module.name}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{module.url}</span>
                        <span>•</span>
                        <span>{module.subModules?.length || 0} Submodules</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleManageSubModules(module)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Manage Sub-modules"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEditModule(module)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Edit Module"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {modulesData && modulesData.lastPage > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={modulesData.lastPage || 1}
                hasNextPage={modulesData.hasMorePages || false}
                hasPrevPage={currentPage > 1}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <ModuleModal
        isOpen={isModuleModalOpen}
        onClose={() => {
          setIsModuleModalOpen(false);
          setEditingModule(null);
        }}
        module={editingModule}
        onSuccess={handleModuleSuccess}
      />

      <SubModuleModal
        isOpen={isSubModuleModalOpen}
        onClose={() => {
          setIsSubModuleModalOpen(false);
          setSelectedModule(null);
        }}
        module={selectedModule}
        onSuccess={handleSubModuleSuccess}
      />
    </div>
  );
}
